import { useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import { RATING_LABELS } from '../../../utils/trafficLight';
import { IMPACT_SCORES, NOT_PROVIDED_SCORE } from '../../../utils/dataProcessor/constants';
import { parseCountryList } from '../../../utils/dataProcessor/parsers';
import { isGlobalSouthCountry } from '../../../utils/countryMapping';

/**
 * Shared data hook for all Progress (Section 2) charts.
 * Call useProgressData() from any chart in the progress/ folder
 * to read from the same memoized data — no prop threading needed.
 */
export function useProgressData() {
  const {
    stats, filteredResults, reportType, filters,
    trafficLights, setTrafficLightOverride,
  } = useApp();

  const w1w2Results = useMemo(
    () => filteredResults.filter(r => r.fundingType !== 'W3Bilateral'),
    [filteredResults]
  );
  const w3Results = useMemo(
    () => filteredResults.filter(r => r.fundingType === 'W3Bilateral'),
    [filteredResults]
  );

  const areasOfWork = useMemo(() => {
    if (trafficLights.computed.length > 0) {
      return trafficLights.computed.map(aow => {
        const override = trafficLights.overrides[aow.name];
        const ratingKey = override ? override.rating : aow.rating;
        return {
          id: aow.name.replace(/\s+/g, '-').toLowerCase(),
          name: aow.name,
          rating: RATING_LABELS[ratingKey],
          ratingKey,
          progress: aow.progress,
          indicators: aow.indicators,
          hlos: aow.hlos || [],
          achievementPct: aow.achievementPct,
          resultCount: aow.resultCount,
          override: override || null,
        };
      });
    }
    return [
      { id: 'aow1', name: 'Area of Work 1', rating: 'No data', ratingKey: 'unknown', progress: 0, indicators: [], override: null },
      { id: 'aow2', name: 'Area of Work 2', rating: 'No data', ratingKey: 'unknown', progress: 0, indicators: [], override: null },
    ];
  }, [trafficLights.computed, trafficLights.overrides]);

  // Funding-split aggregations (single pass over filteredResults)
  const fundingSplits = useMemo(() => {
    const impactAreas = ['gender', 'climate', 'nutrition', 'biodiversity', 'poverty'];
    const impactFields = ['genderLevel', 'climateLevel', 'nutritionLevel', 'biodiversityLevel', 'povertyLevel'];

    const countryByFunding = {};
    const regionByFunding = {};
    const impactByFunding = {};
    const useLevelByFunding = {};
    const readinessLevelByFunding = {};

    impactAreas.forEach(area => {
      impactByFunding[area] = { W1W2: { '-1': 0, 0: 0, 1: 0, 2: 0 }, W3: { '-1': 0, 0: 0, 1: 0, 2: 0 } };
    });

    filteredResults.forEach(r => {
      const bucket = r.fundingType === 'W3Bilateral' ? 'W3' : 'W1W2';

      // Countries
      const countries = r.countriesParsed || (r.countries ? parseCountryList(r.countries) : []);
      const globalSouthOnly = filters?.geographicFocus === 'globalSouth';
      countries.forEach(c => {
        if (globalSouthOnly && !isGlobalSouthCountry(c)) return;
        if (!countryByFunding[c]) countryByFunding[c] = { W1W2: 0, W3: 0 };
        countryByFunding[c][bucket]++;
      });

      // Regions
      const regions = r.regionsParsed || (r.regions ? parseCountryList(r.regions) : []);
      regions.forEach(reg => {
        if (!regionByFunding[reg]) regionByFunding[reg] = { W1W2: 0, W3: 0 };
        regionByFunding[reg][bucket]++;
      });

      // Impact areas
      impactAreas.forEach((area, i) => {
        const rawVal = r[impactFields[i]];
        const score = rawVal ? (IMPACT_SCORES[rawVal] ?? 0) : NOT_PROVIDED_SCORE;
        impactByFunding[area][bucket][score]++;
      });

      // Innovation use level
      if (r.innovationUseLevel != null) {
        const lvl = parseInt(r.innovationUseLevel, 10);
        if (lvl >= 1 && lvl <= 9) {
          if (!useLevelByFunding[lvl]) useLevelByFunding[lvl] = { W1W2: 0, W3: 0 };
          useLevelByFunding[lvl][bucket]++;
        }
      }

      // Readiness level
      if (r.readinessLevel != null) {
        const lvl = parseInt(r.readinessLevel, 10);
        if (lvl >= 1 && lvl <= 9) {
          if (!readinessLevelByFunding[lvl]) readinessLevelByFunding[lvl] = { W1W2: 0, W3: 0 };
          readinessLevelByFunding[lvl][bucket]++;
        }
      }
    });

    return {
      countryResultCountsByFunding: countryByFunding,
      regionResultCountsByFunding: regionByFunding,
      byImpactAreaAndFunding: impactByFunding,
      byInnovationUseLevelAndFunding: useLevelByFunding,
      byReadinessLevelAndFunding: readinessLevelByFunding,
    };
  }, [filteredResults, filters?.geographicFocus]);

  return {
    // Raw from AppContext
    stats,
    filteredResults,
    reportType,
    trafficLights,
    setTrafficLightOverride,

    // Derived
    areasOfWork,
    w1w2Results,
    w3Results,

    // Convenience accessors
    byType: stats?.byType || null,
    byYear: stats?.byYear || null,
    byLevel: stats?.byLevel || null,
    byImpactArea: stats?.byImpactArea || null,
    byFundingType: stats?.byFundingType || null,
    byTypeAndFunding: stats?.byTypeAndFunding || null,
    byTocPathway: stats?.byTocPathway || null,
    byInnovationUseLevel: stats?.byInnovationUseLevel || null,
    byReadinessLevel: stats?.byReadinessLevel || null,
    countries: stats?.countries || [],
    countryResultCounts: stats?.countryResultCounts || {},
    regionResultCounts: stats?.regionResultCounts || {},

    // Funding-split aggregations
    ...fundingSplits,
  };
}
